import React, { useEffect, useMemo, useRef, useState } from "react";
import { utcToDate, utcToLocalDateTime } from "../../components/helper";
import {
	getBlockUsers,
	getPhotos,
	getSelfie,
	unBlockUser,
} from "../../api/apiServiceFetch";
import PhotoModal from "../../components/PhotoModal";
import { FILE_BASE_URL } from "../../config/env";
import { showActionError } from "../../utils/showActionError";
import "./AllProfiles.scss";

const ITEMS_PER_PAGE = 30;

const BlockedProfiles = () => {
	const [users, setUsers] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [expandedUserId, setExpandedUserId] = useState(null);
	const [moreUserId, setMoreUserId] = useState(null);

	const [filterTerm, setFilterTerm] = useState("");
	const [debouncedFilterTerm, setDebouncedFilterTerm] = useState("");

	const [filterCountry, setFilterCountry] = useState("");
	const [filterCity, setFilterCity] = useState("");
	const [filterSex, setFilterSex] = useState(0);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isUnblockingUserId, setIsUnblockingUserId] = useState(0);
	const [userPhotos, setUserPhotos] = useState([]);
	const [userSelfie, setUserSelfie] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [userPhoto, setUserPhoto] = useState(null);

	const modalRef = useRef(null);
	const expandedRef = useRef(null);
	const filePath = FILE_BASE_URL;
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedFilterTerm(filterTerm.trim());
			setPageNumber(1);
		}, 400);

		return () => clearTimeout(timer);
	}, [filterTerm]);
	const fetchUsers = async () => {
		try {
			setLoading(true);

			const response = await getBlockUsers(
				pageNumber,
				ITEMS_PER_PAGE,
				debouncedFilterTerm,
				filterCountry,
				filterCity,
				filterSex,
			);

			const data = response?.data ?? response?.Data ?? [];
			const total = response?.totalCount ?? response?.TotalCount ?? 0;

			setUsers(Array.isArray(data) ? data : []);
			setTotalCount(total);
			setTotalPages(Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)));
		} catch (error) {
			setUsers([]);
			setTotalCount(0);
			setTotalPages(1);
			console.error("Error fetching blocked users:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		void fetchUsers();
	}, [pageNumber, debouncedFilterTerm, filterCountry, filterCity, filterSex]);

	useEffect(() => {
		expandedRef.current = expandedUserId;
	}, [expandedUserId]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				setIsFilterModalOpen(false);
			}
		};

		if (isFilterModalOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isFilterModalOpen]);

	const handleExpandClick = (user) => {
		const userId = Number(user?.userId || 0);
		const isDeleted = isDeletedTrue(user?.isDeleted);

		setExpandedUserId((prev) => {
			if (Number(prev) === userId) {
				setUserPhotos([]);
				setUserSelfie(null);
				return null;
			}

			setUserPhotos([]);
			setUserSelfie(null);

			if (!isDeleted) {
				void fetchUserMedia(userId);
			}

			return userId;
		});
		setMoreUserId((prev) => (Number(prev) === userId ? null : prev));
	};

	const isDeletedTrue = (value) =>
		value === true || value === 1 || value === "1" || value === "true";

	const fetchUserMedia = async (userId) => {
		if (!userId) return;

		try {
			const [photosResp, selfieResp] = await Promise.allSettled([
				getPhotos(userId),
				getSelfie(userId),
			]);

			if (expandedRef.current !== userId) return;

			const photos =
				photosResp.status === "fulfilled" && Array.isArray(photosResp.value)
					? photosResp.value
					: [];
			const selfie =
				selfieResp.status === "fulfilled" && selfieResp.value?.fileName
					? selfieResp.value
					: null;

			setUserPhotos(photos);
			setUserSelfie(selfie);
		} catch {
			if (expandedRef.current !== userId) return;
			setUserPhotos([]);
			setUserSelfie(null);
		}
	};

	const handleFilterRemove = () => {
		setFilterTerm("");
		setFilterCountry("");
		setFilterCity("");
		setFilterSex(0);
		setPageNumber(1);
	};

	const applyFiltersHandler = () => {
		setPageNumber(1);
		setIsFilterModalOpen(false);
	};

	const handlePreviousPage = () => {
		if (pageNumber > 1) setPageNumber((prev) => prev - 1);
	};

	const handleFirstPage = () => {
		setPageNumber(1);
	};
	const handleLastPage = () => {
		setPageNumber(totalPages);
	};
	const handleNextPage = () => {
		if (pageNumber < totalPages) setPageNumber((prev) => prev + 1);
	};

	const handleUnblock = async (userId) => {
		if (!userId || isUnblockingUserId) return;

		try {
			setIsUnblockingUserId(userId);
			await unBlockUser(userId);

			setUsers((prev) =>
				prev.filter((item) => Number(item.userId) !== Number(userId)),
			);
			setTotalCount((prev) => Math.max(0, prev - 1));

			if (Number(expandedUserId) === Number(userId)) {
				setExpandedUserId(null);
				setMoreUserId(null);
			}
		} catch (error) {
			console.error("Failed to unblock user:", error);
			showActionError(error, "Не удалось разблокировать пользователя");
		} finally {
			setIsUnblockingUserId(0);
		}
	};

	const handleViewPhoto = (src) => {
		setUserPhoto(src);
		setShowModal(true);
	};

	const getGenderText = (gender) =>
		Number(gender) === 1 ? "Мужчина" : "Женщина";
	const mapBool = (value) => (value ? "Да" : "Нет");

	const Info = ({ label, value, wide = false, danger = false }) => (
		<div
			className={`info-cell ${wide ? "wide" : ""} ${danger ? "danger" : ""}`}
		>
			<span>{label}</span>
			<strong>{value || "-"}</strong>
		</div>
	);

	const getPrimaryInfo = (user) => [
		{ label: "Возраст", value: user.age || "-" },
		{ label: "Город", value: user.city || "-" },
		{ label: "TG ID", value: user.tgId || "-" },
		{ label: "Причина", value: user.reason || "-" },
	];

	const getMoreInfo = (user) => [
		{
			label: "Никнейм",
			value: user.userName ? (
				<a
					href={`https://t.me/${user.userName}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					@{user.userName}
				</a>
			) : (
				"-"
			),
		},
		{ label: "Telegram имя", value: user.tgFullName || "-" },
		{ label: "Пол", value: getGenderText(user.gender) },
		{ label: "Дата рождения", value: utcToDate(user.birthday) },
		{ label: "Страна", value: user.country || "-" },
		{ label: "Язык", value: user.language || "-" },
		{ label: "Почта", value: user.email || "-" },
		{ label: "OTP", value: user.otp || "-" },
		{ label: "OTP подтверждён", value: mapBool(user.otpConfirmed) },
		{ label: "Активен", value: mapBool(user.isActive) },
		{ label: "Удалён", value: mapBool(user.isDeleted) },
		{ label: "Заморожен", value: mapBool(user.freezeState) },
		{ label: "Инкогнито", value: mapBool(user.incoState) },
		{ label: "Регистрация", value: utcToLocalDateTime(user.createDate) },
		{ label: "Удаление", value: utcToLocalDateTime(user.deleteDate) },
		{ label: "Последний вход", value: utcToLocalDateTime(user.lastLoginDate) },
	];

	const rows = useMemo(() => users ?? [], [users]);

	const isInitialLoading = loading && users.length === 0 && totalCount === 0;

	return (
		<div className="AllProfiles compactProfiles">
			{showModal && (
				<PhotoModal photo={userPhoto} onClose={() => setShowModal(false)} />
			)}

			<div className="profiles-toolbar">
				<div className="profiles-search">
					<input
						type="text"
						placeholder="Поиск..."
						value={filterTerm}
						onChange={(event) => {
							setFilterTerm(event.target.value);
							setPageNumber(1);
						}}
					/>
				</div>

				<button type="button" onClick={() => setIsFilterModalOpen(true)}>
					Фильтр
				</button>
				<button type="button" className="ghost" onClick={handleFilterRemove}>
					Сброс
				</button>

				<span className="profiles-count">Всего: {totalCount}</span>
			</div>
			{isInitialLoading ? (
				<div className="profiles-loading">Загрузка...</div>
			) : null}

			{loading && !isInitialLoading ? (
				<div className="profiles-inline-loading">Обновление...</div>
			) : null}
			{isFilterModalOpen && (
				<div className="filter-modal">
					<div className="filter-modal-content compact-modal" ref={modalRef}>
						<h3>Фильтр</h3>
						<input
							type="text"
							placeholder="Страна"
							value={filterCountry}
							onChange={(event) => setFilterCountry(event.target.value)}
						/>
						<input
							type="text"
							placeholder="Город"
							value={filterCity}
							onChange={(event) => setFilterCity(event.target.value)}
						/>
						<select
							value={filterSex}
							onChange={(event) => setFilterSex(Number(event.target.value))}
						>
							<option value={0}>Пол</option>
							<option value={1}>Мужчина</option>
							<option value={2}>Женщина</option>
						</select>

						<div className="modal-row-actions">
							<button type="button" onClick={applyFiltersHandler}>
								Применить
							</button>
							<button
								type="button"
								className="ghost"
								onClick={() => setIsFilterModalOpen(false)}
							>
								Отмена
							</button>
						</div>
					</div>
				</div>
			)}

			{!isInitialLoading && users.length === 0 ? (
				<div className="profiles-empty">Нет профилей</div>
			) : null}

			{rows.map((user) => {
				const isOpen = Number(expandedUserId) === Number(user.userId);
				const isMoreOpen = Number(moreUserId) === Number(user.userId);
				const isUnblocking = Number(isUnblockingUserId) === Number(user.userId);
				const isDeleted = isDeletedTrue(user.isDeleted);

				return (
					<div
						key={user.userId}
						className={`profile-card ${isOpen ? "open" : ""}`}
					>
						<button
							type="button"
							className={`profile-summary ${Number(user.gender) === 1 ? "male" : "female"}`}
							onClick={() => handleExpandClick(user)}
						>
							<div className="summary-main">
								<strong>
									{user.fullName ||
										`${user.name || ""} ${user.surName || ""}`.trim() ||
										"Без имени"}{" "}
									<span>#{user.userId}</span>
								</strong>

								<div className="summary-meta">
									<span>
										{user.userName ? (
											<a
												href={`https://t.me/${user.userName}`}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
											>
												@{user.userName}
											</a>
										) : (
											"@-"
										)}
									</span>
									<span>{getGenderText(user.gender)}</span>
									<span>{user.age ? `${user.age} лет` : "Возраст -"}</span>
									<span>{user.city || "Город -"}</span>
								</div>
							</div>

							<div className="summary-right">
								<span className="open-indicator">{isOpen ? "−" : "+"}</span>
							</div>
						</button>

						{isOpen && (
							<div className="profile-details">
								<div className="profile-content-grid">
									<section className="media-panel">
										<div className="media-header">
											<strong>Фото</strong>
										</div>

										<div className="media-grid">
											{isDeleted ? (
												<div className="media-empty">Пользователь удалён</div>
											) : (
												<>
													{userSelfie?.fileName ? (
														<div className="media-item selfie">
															<img
																src={`${filePath}/Selfie/${user.userId}/${userSelfie.fileName}`}
																onClick={() =>
																	handleViewPhoto(
																		`${filePath}/Selfie/${user.userId}/${userSelfie.fileName}`,
																	)
																}
																alt="Селфи"
															/>
														</div>
													) : (
														<div className="media-empty">Нет селфи</div>
													)}

													{Array.isArray(userPhotos) &&
													userPhotos.length > 0 ? (
														userPhotos.map((photo, index) => (
															<div
																key={photo.id || `${photo.fileName}-${index}`}
																className="media-item"
															>
																<img
																	src={`${filePath}/Photo/${user.userId}/${photo.fileName}`}
																	onClick={() =>
																		handleViewPhoto(
																			`${filePath}/Photo/${user.userId}/${photo.fileName}`,
																		)
																	}
																	alt={`Фото ${index + 1}`}
																/>
															</div>
														))
													) : (
														<div className="media-empty">Нет фото</div>
													)}
												</>
											)}
										</div>
									</section>

									<section className="profile-info-panel">
										<div className="primary-info-grid">
											{getPrimaryInfo(user).map((item) => (
												<Info
													key={item.label}
													label={item.label}
													value={item.value}
												/>
											))}

											<button
												type="button"
												className="more-button"
												onClick={() =>
													setMoreUserId((prev) =>
														prev === user.userId ? null : user.userId,
													)
												}
											>
												{isMoreOpen ? "Скрыть" : "Подробнее"}
											</button>
										</div>

										{isMoreOpen && (
											<div className="more-info-grid">
												{getMoreInfo(user).map((item) => (
													<Info
														key={item.label}
														label={item.label}
														value={item.value}
													/>
												))}
											</div>
										)}

										<div
											className="profile-actions-compact"
											style={{ gridTemplateColumns: "minmax(180px, 280px)" }}
										>
											<button
												type="button"
												disabled={isUnblocking}
												style={{ background: "#18845b" }}
												onClick={() => handleUnblock(user.userId)}
											>
												{isUnblocking ? "Разблокировка..." : "Разблокировать"}
											</button>
										</div>
									</section>
								</div>
							</div>
						)}
					</div>
				);
			})}

			<div className="pagination">
				<button onClick={handleFirstPage} disabled={pageNumber === 1}>
					На первую
				</button>
				<button onClick={handlePreviousPage} disabled={pageNumber === 1}>
					Назад
				</button>
				<span>
					{pageNumber} / {totalPages}
				</span>
				<button onClick={handleNextPage} disabled={pageNumber === totalPages}>
					Дальше
				</button>

				<button onClick={handleLastPage} disabled={pageNumber === totalPages}>
					На последнюю
				</button>
			</div>
		</div>
	);
};

export default BlockedProfiles;
